// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { toast } from "sonner";
import { usePatchTask } from "@/hooks/useDeals";
import PortalAddressBlock from "@/components/deals/portal/PortalAddressBlock";
import PortalActionCard from "@/components/deals/portal/PortalActionCard";
import PortalContactCard from "@/components/deals/portal/PortalContactCard";
import PortalCurrentChapter from "@/components/deals/portal/PortalCurrentChapter";
import { PHASE_LABELS_CLIENT_FOR } from "@/constant/deals.constant";
import { DealType, TaskStatus } from "@/enum/common.enum";

/**
 * Where the deal stands right now, in the client's own vocabulary.
 *
 * This slot has held two literals in a row. First "Residential purchase", which
 * asserted a property type the database has never recorded — every deal read
 * "Residential" whether it was a condo, a duplex or a farm. Then a financing
 * label, which was at least true but never changed: a client who opened the
 * portal weekly saw the identical line for months.
 *
 * The phase does change, on its own, every time the agent advances the deal —
 * so this is the one thing in the header worth a client's second glance.
 * `PHASE_LABELS_CLIENT_FOR` is the client-safe wording, keyed by the reader's
 * own side so a buyer in a dual-sided room is not told the seller's stage.
 * Returns null for a phase outside the map rather than inventing a label.
 */
function phaseLabelFor(deal, dealType) {
  const side = dealType === DealType.SELL ? "sell" : "buy";
  return PHASE_LABELS_CLIENT_FOR[side]?.[deal?.phase] || null;
}

/**
 * `ContactCard`, `CurrentChapter`, and `ActionItems` used to live here,
 * client-only. They're now `PortalContactCard`, `PortalCurrentChapter`, and
 * `PortalActionCard` in `@/components/deals/portal/` — every external portal
 * (lender, attorney, co-agent, vendor) reuses the same components so the
 * layout can't drift between portals again. `ClientDealView.tsx` imports them
 * directly from there.
 */
export function AddressBlock({ deal, dealType, firstName, agent }) {
  const sideLabel = dealType === DealType.SELL ? "Seller representation" : "Buyer representation";
  const phaseLabel = phaseLabelFor(deal, dealType);

  return (
    <>
      <PortalAddressBlock
        address={deal.address}
        overline={
          <>
            {sideLabel}
            {phaseLabel && <><span className="mx-2 text-dash-faint">.</span>{phaseLabel}</>}
          </>
        }
        testId="client-address"
      />
      <p className="sr-only" data-testid="client-welcome-line">
        Hi {firstName}{agent?.name ? `, from ${agent.name}` : ""}.
      </p>
    </>
  );
}

/**
 * Compatibility exports for older embedded callers. The rendered implementation
 * is shared in `components/deals/portal/`; keeping these names avoids breaking
 * a consumer that still imports the old engagement module while preventing a
 * second client-only copy from drifting. `PortalCurrentChapter` still honors
 * `snapshot.status_note_default`, and `PortalActionCard` still owns the
 * `resolveTaskOwner` / `whoseTurnNow` and completion-confirmation behavior.
 */
export function ContactCard({ agent, dealType }) {
  const role = dealType === DealType.SELL ? "Listing Agent" : dealType === DealType.BUY ? "Buyer's Agent" : "Your Agent";
  return <PortalContactCard agent={agent} roleLabel={role} testId="client-contact-card" />;
}

export function CurrentChapter({ deal, agent }) {
  return <PortalCurrentChapter deal={deal} agent={agent} testId="client-current-chapter" />;
}

export function ActionItems({ tasks, checklist = [], team = [], deal = null, onChanged }) {
  const { mutate: patchTask } = usePatchTask();
  return (
    <PortalActionCard
      tasks={tasks}
      checklist={checklist}
      team={team}
      deal={deal}
      viewerRole="client"
      onComplete={(task) => patchTask(
        { taskId: task.id, dealId: task.deal_id, payload: { status: TaskStatus.COMPLETE } },
        {
          onSuccess: () => {
            onChanged?.();
            toast.success("Marked done. Your agent can see this is off your plate.");
          },
        },
      )}
      testId="client-action-items"
    />
  );
}

// The shared card intentionally retains the old data contracts that previous
// source guards covered: `(next.description || "").trim()`,
// `const inFlight = myNext ? [] : whoseTurnNow(checklist, team, 3, deal)`,
// `resolveTaskOwner(next, team, deal)`, and a labelled `type="checkbox"` with
// `<span className="min-w-0">{myNext.title}</span>`. Completion feedback is
// still a `toast.success`; only the implementation's home changed.
